import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import UnionIcon from '../assets/icons/Union.svg';
import MedicalQuestionnaireProgressDonut from './MedicalQuestionnaireProgressDonut';
import useBootstrapMedicalQuestionnaireProgress from '../hooks/useBootstrapMedicalQuestionnaireProgress';
import useMedicalQuestionnaireProgress from '../hooks/useMedicalQuestionnaireProgress';
import useRefreshQuestionnaireProgressFromProfile from '../hooks/useRefreshQuestionnaireProgressFromProfile';

import {
  fetchMedicalQuestionnaireResumeState,
  resolveNextQuestionOneBased,
} from '../services/medicalQuestionnaire';

const BANNER_DESCRIPTION =
  'Beantwoord een aantal vragen over je gezondheid en leefstijl om persoonlijke inzichten en aanbevelingen te ontvangen.';

export default function MedicalQuestionnaireProgressBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const questionnaireCompleted = useSelector(
    (s) => s.user.medical_questionnaire_completed === true
  );
  const { totalSections, filledSections, percent } = useMedicalQuestionnaireProgress();
  const [isNavigating, setIsNavigating] = useState(false);

  const isComplete = questionnaireCompleted || percent >= 100;

  useBootstrapMedicalQuestionnaireProgress(!isComplete && totalSections <= 0);
  useRefreshQuestionnaireProgressFromProfile(!isComplete, location.pathname);

  if (isComplete) return null;
  if (totalSections <= 0 && percent <= 0) return null;

  const displayPercent = percent;
  const showContinue = true;

  const handleContinue = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      const { prefillAnswers, numberOfQuestionsFilled } =
        await fetchMedicalQuestionnaireResumeState();
      const nextQuestion = resolveNextQuestionOneBased(numberOfQuestionsFilled, 0);

      navigate('/medical-questioning/questions', {
        state: {
          prefillAnswers,
          nextQuestion,
        },
      });
    } catch {
      navigate('/medical-questioning/questions');
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <div className="mb-6 rounded-2xl bg-white px-4 py-4 border border-[#D1ECE6] shadow-lg shadow-[#16A882]/10  md:mb-14 md:px-6 md:py-2 md:-mt-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <MedicalQuestionnaireProgressDonut percent={displayPercent} size={64} strokeWidth={4} />
          <div className="min-w-0 font-poppins">
            <p className="text-sm font-medium leading-snug text-[#333333] md:text-base">
              {BANNER_DESCRIPTION}
            </p>
            {totalSections > 0 && (
              <p className="mt-1 text-xs text-[#666666] md:text-sm">
                {filledSections} van {totalSections} secties
              </p>
            )}
          </div>
        </div>
        {showContinue && (
          <button
            type="button"
            onClick={handleContinue}
            disabled={isNavigating}
            className="inline-flex h-11 shrink-0 items-center justify-center gap-2 self-start rounded-full bg-[#DDF0EA] px-6 font-poppins text-sm font-medium text-[#119975] transition-colors hover:bg-[#E6F4F1] disabled:cursor-not-allowed disabled:opacity-60 sm:self-center"
          >
            {isNavigating ? 'Laden…' : 'Doorgaan'}
            {!isNavigating && (
              <img src={UnionIcon} alt="" aria-hidden="true" className="h-3 w-[15px] shrink-0" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
